import type { RacemapEvent } from '@racemap/utilities/types/events';
import { useEventsPaginated } from 'src/lib/customHooks/useEventsPaginated';
import { useDebounceValue } from 'usehooks-ts';

export const MinQueryLength = 2;
const DebounceTimeInMillis = 250;

export interface EventSearchItem {
  kind: 'event';
  key: string;
  eventId: string;
  path: string;
  event: RacemapEvent;
}

/** A query the user searched before. Picking it refills the field rather than navigating. */
export interface RecentSearchItem {
  kind: 'recent';
  key: string;
  query: string;
}

export type SearchItem = EventSearchItem | RecentSearchItem;

export interface SearchGroup {
  key: string;
  title: string;
  items: Array<SearchItem>;
}

interface SearchResults {
  groups: Array<SearchGroup>;
  eventItems: Array<EventSearchItem>;
  hasError: boolean;
  retry: () => void;
  /** The query the results actually answer — what the rows highlight their match against. */
  debouncedQuery: string;
  /** True once the query is long enough to search for. */
  isActive: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  loadNextPage: () => void;
}

/**
 * Groups are the seam a further category is added through, without GlobalSearch or SearchPanel
 * needing to know about it.
 */
export const useSearchResults = (query: string): SearchResults => {
  const trimmedQuery = query.trim();
  const [debouncedQuery] = useDebounceValue(trimmedQuery, DebounceTimeInMillis);
  // The live query has to be long enough as well as the debounced one. Gating on the debounced
  // value alone leaves the previous results showing for one debounce after the field is cleared
  // or shortened, which reads as the panel flashing back up.
  const isActive = trimmedQuery.length >= MinQueryLength && debouncedQuery.length >= MinQueryLength;

  // Mirrors the Tracking Maps list, so search never offers an event the user cannot open.
  const { data, isLoading, error, retry, hasNextPage, loadNextPage } = useEventsPaginated({
    searchValue: debouncedQuery,
    show: ['hidden', 'parentsonly'],
    filter: 'can-edit',
    fetchDisabled: !isActive,
    // Rows here are actionable, so they must never outlive the query they answer: keeping the
    // previous ones would let ⏎ open an event from the query before this one.
    keepPreviousData: false,
    pageSize: 10,
  });

  // Failure suppresses the rows for the same reason.
  const hasError = error != null;
  const items =
    isActive && !hasError
      ? // Paged Meilisearch results can repeat an event across page boundaries; the same guard the
        // event select applies.
        data
          .filter((event, index) => index === data.findIndex((other) => other.id === event.id))
          .map(
            (event): EventSearchItem => ({
              kind: 'event',
              key: `event-${event.id}`,
              eventId: event.id,
              path: `/admin/events/${event.id}`,
              event,
            }),
          )
      : [];

  return {
    groups: items.length > 0 ? [{ key: 'events', title: 'Events', items }] : [],
    eventItems: items,
    debouncedQuery,
    isActive,
    isLoading: isActive && isLoading,
    hasError: isActive && hasError,
    retry,
    hasNextPage: isActive && !hasError && hasNextPage,
    loadNextPage,
  };
};
